// "Makes & models" — brand volume, calibration (the OEM-qualification signal)
// and estimate intensity. Every bar cross-filters the room by make.

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts"
import { ChapterSection, ChartCard, MakePillRow, BarTip, Highlight } from "../room-chrome"
import { AXIS_TICK, CAT, GRID_STROKE, RAMP, fmt$, fmt$full } from "../room-model"
import type { HighlightAction, HighlightFinding, MakeInsights, deriveVehicleMix } from "../derive"

type VehicleMix = ReturnType<typeof deriveVehicleMix>

const adasColor = (rate: number) => (rate >= 40 ? CAT.deep : rate >= 15 ? CAT.brand : rate > 0 ? CAT.light : "#D4D4D8")

export function MakesChapter({ makes, vehicleMix, make, onToggleMake, highlight, onHighlight, rangeLabel }: {
  makes: MakeInsights
  vehicleMix: VehicleMix
  make: string
  onToggleMake: (m: string) => void
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  rangeLabel: string
}) {
  const { pills } = makes
  const total = pills.totalCount

  const countData = makes.byCount.map(r => ({
    make: r.make, value: r.count, dim: r.dim,
    tip: `${r.count} estimate${r.count !== 1 ? "s" : ""} (${total ? Math.round((r.count / total) * 100) : 0}% of total)`,
  }))
  const adasData = makes.byAdas.map(r => ({
    make: r.make, value: r.adasRate, dim: r.dim, rate: r.adasRate,
    tip: `${r.adasRate}% — ${r.adasCount} of ${r.count} estimate${r.count !== 1 ? "s" : ""}${r.calibrationTypes.length ? ` · Types: ${r.calibrationTypes.join("; ")}` : ""}`,
  }))
  const valueData = makes.byAvg.map(r => ({
    make: r.make, value: Math.round(r.avg), dim: r.dim,
    tip: `${fmt$full(r.avg)} avg · ${r.count} estimate${r.count !== 1 ? "s" : ""}`,
  }))

  const volumePills = pills.topVolume ? [
    { name: "Top volume", value: pills.topVolume.make, count: `${pills.topVolume.count} est · ${total ? Math.round((pills.topVolume.count / total) * 100) : 0}%`, tone: "high" as const },
    { name: "Makes seen", value: String(pills.makesSeen), count: "across the period" },
    { name: "Top 3 share", value: `${pills.top3Share}%`, count: pills.top3Names.join(" · "), tone: "high" as const },
  ] : []
  const adasPills = makes.byAdas.filter(r => r.adasRate > 0).slice(0, 4).map(r => ({
    name: r.make, value: `${r.adasRate}%`, count: `${r.adasCount} of ${r.count} est.`,
    tone: (r.adasRate >= 40 ? "high" : r.adasRate >= 15 ? "mid" : "low") as "high" | "mid" | "low",
  }))
  const valuePills = pills.topAvg && pills.topVolume ? [
    { name: "Top make", value: pills.topAvg.make, count: `${fmt$(pills.topAvg.avg)} avg`, tone: "high" as const },
    { name: `vs ${pills.topVolume.make}`, value: `${pills.vsTopVolume.toFixed(1)}×`, count: "higher avg value", tone: "high" as const },
    { name: "Network avg", value: fmt$(makes.networkAvg), count: "all estimates" },
  ] : []

  const empty = makes.rows.length === 0

  return (
    <ChapterSection id="makes" eyebrow="Vehicle-make insights" title="Brand volume, calibration, and estimate intensity" audience="For leadership">
      {empty ? (
        <ChartCard title="Estimates by make" sub="Service drive volume per brand">
          <p className="h-[180px] grid place-items-center text-sm text-[#8B897E]">No vehicle-make data in this view.</p>
        </ChartCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Estimates by make */}
          <ChartCard title="Estimates by make" sub="Volume per brand — click a make to filter the room">
            {({ expanded }) => (
              <>
                <div style={{ height: expanded ? 320 : 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={countData}>
                      <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                      <XAxis dataKey="make" tick={AXIS_TICK} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={44} />
                      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                      <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={44} className="cursor-pointer"
                        onClick={(e: { make?: string }) => e?.make && onToggleMake(e.make)}>
                        {countData.map(r => <Cell key={r.make} fill={CAT.brand} opacity={r.dim ? 0.3 : 1} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <MakePillRow pills={volumePills} />
              </>
            )}
          </ChartCard>

          {/* ADAS & calibration rate by make */}
          <ChartCard title="ADAS & calibration rate by make" sub="% of estimates needing a calibration scan — OEM repairer qualification signal">
            {({ expanded }) => (
              <>
                <div style={{ height: expanded ? 320 : 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={adasData}>
                      <CartesianGrid vertical={false} stroke={GRID_STROKE} />
                      <XAxis dataKey="make" tick={AXIS_TICK} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={44} />
                      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={34} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                      <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={44} className="cursor-pointer"
                        onClick={(e: { make?: string }) => e?.make && onToggleMake(e.make)}>
                        {adasData.map(r => <Cell key={r.make} fill={adasColor(r.rate)} opacity={r.dim ? 0.3 : 1} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {adasPills.length > 0 ? <MakePillRow pills={adasPills} /> : <p className="text-[11px] text-[#8B897E] mt-3">No calibration-flagged estimates in this view.</p>}
              </>
            )}
          </ChartCard>

          {/* Average estimate value by make — full width */}
          <ChartCard title="Average estimate value by make" sub="Repair-cost intensity per brand — OEM warranty & preferred-repairer input" className="lg:col-span-2">
            {({ expanded }) => (
              <>
                {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
                <div style={{ height: Math.max(valueData.length * (expanded ? 34 : 26) + 24, 140) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={valueData} layout="vertical" margin={{ left: 16, right: 16 }}>
                      <CartesianGrid horizontal={false} stroke={GRID_STROKE} />
                      <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={v => `$${Math.round(v / 1000)}k`} />
                      <YAxis type="category" dataKey="make" tick={AXIS_TICK} tickLine={false} axisLine={false} width={80} />
                      <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22} className="cursor-pointer"
                        onClick={(e: { make?: string }) => e?.make && onToggleMake(e.make)}>
                        {valueData.map((r, i) => <Cell key={r.make} fill={i === 0 ? CAT.deep : CAT.brand} opacity={r.dim ? 0.3 : 1} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <MakePillRow pills={valuePills} />
              </>
            )}
          </ChartCard>

          {/* Top models by identified $ (was Vehicle mix) */}
          <ChartCard title="Top models by identified $" sub={`What's being hit · ${rangeLabel}`} className="lg:col-span-2">
            {vehicleMix.rows.length === 0 ? (
              <p className="h-[120px] grid place-items-center text-sm text-[#8B897E]">No vehicle data in this view.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5 mt-2">
                {vehicleMix.rows.map(v => (
                  <div key={v.name}>
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <p className="text-sm text-zinc-800 truncate">{v.name}</p>
                      <p className="text-xs text-[#8B897E] tabular-nums shrink-0"><span className="font-semibold text-zinc-700">{fmt$(v.identified)}</span> · {v.count}</p>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#F1F0EB] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(v.identified / vehicleMix.max) * 100}%`, background: RAMP[1] }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      )}
    </ChapterSection>
  )
}
